import { useTranslation } from "react-i18next";
import * as Yup from "yup";
import * as Const from "../constants/LiveMonitoringConstants";
import {addFilter, getFilterValue, renderMultiSelect} from "../../../utils/FilterUtils";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { STATE_ACTIVE_FILTERS } from "../constants/LiveMonitoringConfigurationRecoil";
import AdvancedFilter from "../../../component/AdvancedFilter";
import * as Renders from "../../../component/AdvancedFiltersRenders";
import { useLocation, useNavigate } from "react-router-dom";
import { offsetDateJsConvert } from "../util/CashbackUtil";
import { Box, Grid, InputAdornment, Typography } from "@mui/material";
import { ArrowBackRounded, Euro, Percent } from "@mui/icons-material";
import { GlobalContext } from "../../../../../util/state/GlobalContextProvider";
import {useContext, useState} from "react";
import {ID_FILTER_DEALERS, ID_FILTER_LEGAL_ENTITIES} from "../constants/LiveMonitoringConstants";


function LiveMonitoringFilter() {
    const navigate = useNavigate();
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const dateFrom = queryParams.get('dateFrom'); 
    const dateTo = queryParams.get('dateTo');

    const { t } = useTranslation("finance", { keyPrefix: "cashback" });
    const { t: tg } = useTranslation("translation");

    const setFilters = useSetRecoilState(STATE_ACTIVE_FILTERS);
    const filterDefs = useRecoilValue(STATE_ACTIVE_FILTERS);

    const {  globalData: { user }, } = useContext(GlobalContext);

    const legalEntityOptions: any[] = user.legalEntities
        ?.filter((x: any) => x.name.toLowerCase() !== "all")
        .map((x: any) => ({
            value: x.name,
            label: tg(`legalEntities.${x.name}`),
        }));

    const dealerOptions: any[] = user.dealers
        ?.filter((x: any) => x.name.toLowerCase() !== "all")
        .map((x: any) => ({
            value: x.name,
            label: tg(`dealers.${x.name}`),
        }));

    const [dealers, setDealers] = useState<any[]>(dealerOptions);

    const schema = Yup.object().shape({
        [Const.ID_FILTER_CUSTOMERNAME_LM]: Yup.string().max(
            128,
            t("filters.errors.invalid-length")
        ),
        [Const.ID_FILTER_TYPOLOGY_LM]: Yup.string(),
        [Const.ID_FILTER_PERCENTAGE_FROM_LM]: Yup.number().transform((value, originalValue) => {
            return originalValue.trim() === '' ? undefined : parseFloat(originalValue);
        }).max(100, t("filters.errors.invalid-percentage")).nullable(),
        [Const.ID_FILTER_PERCENTAGE_TO_LM]: Yup.number().transform((value, originalValue) => {
            return originalValue.trim() === '' ? undefined : parseFloat(originalValue);
        }).max(100, t("filters.errors.invalid-percentage")).test('percentage', t('filters.errors.invalid-percentage'), function (value) {
            const fromValue = this.parent[Const.ID_FILTER_PERCENTAGE_FROM_LM];
            return fromValue === undefined || value === undefined || value >= fromValue;
        }).nullable(),
        [Const.ID_FILTER_THRESHOLD_FROM_LM]: Yup.number().transform((value, originalValue) => {
            return originalValue.trim() === '' ? undefined : parseFloat(originalValue);
        }).test('threshold', t('filters.errors.invalid-threshold'), function (value) {
            const toValue = this.parent[Const.ID_FILTER_THRESHOLD_TO_LM];
            return toValue === undefined || value === undefined || value <= toValue;
        }).nullable(),
        [Const.ID_FILTER_THRESHOLD_TO_LM]: Yup.number().transform((value, originalValue) => {
            return originalValue.trim() === '' ? undefined : parseFloat(originalValue);
        }).test('threshold', t('filters.errors.invalid-threshold'), function (value) {
            const fromValue = this.parent[Const.ID_FILTER_THRESHOLD_FROM_LM];
            return fromValue === undefined || value === undefined || value >= fromValue;
        }).nullable(),
    });


    const defaultValues = {
        [Const.ID_FILTER_DATE_FROM_LM]: dateFrom ? offsetDateJsConvert(dateFrom, 'DD/MM/YYYY') : null,
        [Const.ID_FILTER_DATE_TO_LM]: dateTo ? offsetDateJsConvert(dateTo, 'DD/MM/YYYY') : null,
        [Const.ID_FILTER_CUSTOMERNAME_LM]: getFilterValue(Const.ID_FILTER_CUSTOMERNAME_LM, filterDefs) || "",
        [Const.ID_FILTER_TYPOLOGY_LM]: getFilterValue(Const.ID_FILTER_TYPOLOGY_LM, filterDefs) || "",
        [Const.ID_FILTER_THRESHOLD_FROM_LM]: getFilterValue(Const.ID_FILTER_THRESHOLD_FROM_LM, filterDefs) || "",
        [Const.ID_FILTER_THRESHOLD_TO_LM]: getFilterValue(Const.ID_FILTER_THRESHOLD_TO_LM, filterDefs) || "",
        [Const.ID_FILTER_PERCENTAGE_FROM_LM]: getFilterValue(Const.ID_FILTER_PERCENTAGE_FROM_LM, filterDefs) || "",
        [Const.ID_FILTER_PERCENTAGE_TO_LM]: getFilterValue(Const.ID_FILTER_PERCENTAGE_TO_LM, filterDefs) || "",
        [Const.ID_FILTER_ACCRUED_FROM_LM]: getFilterValue(Const.ID_FILTER_ACCRUED_FROM_LM, filterDefs) || "",
        [Const.ID_FILTER_ACCRUED_TO_LM]: getFilterValue(Const.ID_FILTER_ACCRUED_TO_LM, filterDefs) || "",
        legalEntities:
            getFilterValue(ID_FILTER_LEGAL_ENTITIES, filterDefs) ||
            legalEntityOptions.map((x: any) => x.value),
        dealers: getFilterValue(ID_FILTER_DEALERS, filterDefs) || [],
    };

    const customDefaultValues = {
        [Const.ID_FILTER_DATE_FROM_LM]: dateFrom ? offsetDateJsConvert(dateFrom, 'DD/MM/YYYY') : null,
        [Const.ID_FILTER_DATE_TO_LM]: dateTo ? offsetDateJsConvert(dateTo, 'DD/MM/YYYY') : null,
        [Const.ID_FILTER_CUSTOMERNAME_LM]: "",
        [Const.ID_FILTER_TYPOLOGY_LM]:  "",
        [Const.ID_FILTER_THRESHOLD_FROM_LM]:  "",
        [Const.ID_FILTER_THRESHOLD_TO_LM]: "",
        [Const.ID_FILTER_PERCENTAGE_FROM_LM]: "",
        [Const.ID_FILTER_PERCENTAGE_TO_LM]: "",
        [Const.ID_FILTER_ACCRUED_FROM_LM]:  "",
        [Const.ID_FILTER_ACCRUED_TO_LM]: "",
        legalEntities:
            getFilterValue(ID_FILTER_LEGAL_ENTITIES, filterDefs) ||
            legalEntityOptions.map((x: any) => x.value),
        dealers: [],
    };

    const fields = {
        main: {
            [Const.ID_FILTER_CUSTOMERNAME_LM]: Renders.renderTextField(
                Const.ID_FILTER_CUSTOMERNAME_LM,
                t("livemonitoring.filters." + Const.ID_FILTER_CUSTOMERNAME_LM)
            ),
            [Const.ID_FILTER_TYPOLOGY_LM]: Renders.renderSelect(
                Const.ID_FILTER_TYPOLOGY_LM,
                t("livemonitoring.filters." + Const.ID_FILTER_TYPOLOGY_LM),
                Const.transactionType.map((x) => {
                    return { ...x, label: t(x.label) };
                })
            )
        },
        adv: {
            [Const.ID_FILTER_THRESHOLD_FROM_LM]: Renders.renderTextField(
                Const.ID_FILTER_THRESHOLD_FROM_LM,
                t("livemonitoring.filters." + Const.ID_FILTER_THRESHOLD_FROM_LM),
                "number",
                {
                    startAdornment:
                        <InputAdornment position="start">
                            <Euro style={{ fontSize: '15px' }} />
                        </InputAdornment>

                }
            ),
            [Const.ID_FILTER_THRESHOLD_TO_LM]: Renders.renderTextField(
                Const.ID_FILTER_THRESHOLD_TO_LM,
                t("livemonitoring.filters." + Const.ID_FILTER_THRESHOLD_TO_LM),
                "number",
                {
                    startAdornment:
                        <InputAdornment position="start">
                            <Euro style={{ fontSize: '15px' }} />
                        </InputAdornment>

                }
            ),
            [Const.ID_FILTER_PERCENTAGE_FROM_LM]: Renders.renderTextField(
                Const.ID_FILTER_PERCENTAGE_FROM_LM,
                t("livemonitoring.filters." + Const.ID_FILTER_PERCENTAGE_FROM_LM),
                "number",
                {
                    startAdornment:
                        <InputAdornment position="start">
                            <Percent style={{ fontSize: '15px' }} />
                        </InputAdornment>

                }
            ),
            [Const.ID_FILTER_PERCENTAGE_TO_LM]: Renders.renderTextField(
                Const.ID_FILTER_PERCENTAGE_TO_LM,
                t("livemonitoring.filters." + Const.ID_FILTER_PERCENTAGE_TO_LM),
                "number",
                {
                    startAdornment:
                        <InputAdornment position="start">
                            <Percent style={{ fontSize: '15px' }} />
                        </InputAdornment>

                }
            ),
            [Const.ID_FILTER_ACCRUED_FROM_LM]: Renders.renderTextField(
                Const.ID_FILTER_ACCRUED_FROM_LM,
                t("livemonitoring.filters." + Const.ID_FILTER_ACCRUED_FROM_LM),
                "number",
                {
                    startAdornment:
                        <InputAdornment position="start">
                            <Euro style={{ fontSize: '15px' }} />
                        </InputAdornment>

                }
            ),
            [Const.ID_FILTER_ACCRUED_TO_LM]: Renders.renderTextField(
                Const.ID_FILTER_ACCRUED_TO_LM,
                t("livemonitoring.filters." + Const.ID_FILTER_ACCRUED_TO_LM),
                "number",
                {
                    startAdornment:
                        <InputAdornment position="start">
                            <Euro style={{ fontSize: '15px' }} />
                        </InputAdornment>

                }
            ),
            legalEntities: renderMultiSelect(
                tg("shared.legalEntities"),
                legalEntityOptions,
                "legalEntities",
                legalEntityOptions.length <= 1,
                (value: any, setValue: any) => {
                    setValue('dealers', []);
                    let dealers = user?.dealers
                        ?.filter(
                            (x: any) =>
                                value?.length > 0 &&
                                x.legalEntities.some((e: any) => value.includes(e.name)) &&
                                x.name.toLowerCase() !== "all"
                        )
                        .map((x: any) => {
                            return {
                                label: tg(`dealers.${x.name}`),
                                value: x.name,
                            };
                        });
                    setDealers(dealers);
                }
            ),
            dealers: renderMultiSelect(tg("shared.dealers"), dealers, "dealers", dealerOptions.length <= 1)

        }
    };

    const submit = (data: any) => {
        Object.keys(defaultValues)
            .forEach((param) => {
                addFilter(setFilters, param, data[param]);
            });
        addFilter(setFilters, Const.ID_FILTER_ACCRUED_FROM_LM, data[Const.ID_FILTER_ACCRUED_FROM_LM]);
        addFilter(setFilters, Const.ID_FILTER_ACCRUED_TO_LM, data[Const.ID_FILTER_ACCRUED_TO_LM]);
        addFilter(setFilters, ID_FILTER_LEGAL_ENTITIES, data.legalEntities);
        addFilter(setFilters, ID_FILTER_DEALERS, data.dealers);

    };

    const reset = () => {
        setFilters([]);
    };

    return (
        <Grid>
            <Grid item xs={12}>
                <Box color={'primary.light'} style={{ display: 'flex', alignItems: 'center', gap: '1ch' }}>
                    <ArrowBackRounded onClick={() => {
                        navigate("/finance/cashback");
                    }} style={{ cursor: 'pointer' }} />
                    <Typography variant={'body2'} color={'primary.light'} fontWeight={700} textTransform={'uppercase'}>
                        {(dateFrom && dateTo) ? dateFrom + " - " + dateTo : ''}
                    </Typography>
                </Box>
            </Grid>


            <AdvancedFilter
                schema={schema}
                mainFields={fields.main}
                fields={fields.adv}
                defaultValues={defaultValues}
                onSubmit={submit}
                onReset={reset}
                customDefaultValues={customDefaultValues}
            />
        </Grid>
    );
}

export default LiveMonitoringFilter;
